import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { WindowRef } from '@spartacus/core';

export interface Auth0Config {
  domain: string;
  clientId: string;
  audience?: string;
  redirectUri: string;
  logoutUrl: string
}

export interface Auth0TokenResponse {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
  scope?: string;
}

const CODE_VERIFIER_KEY = 'auth0_code_verifier';
const STATE_KEY = 'auth0_state';
const NONCE_KEY = 'auth0_nonce';

@Injectable({
  providedIn: 'root',
})
export class Auth0Service {
  private readonly config: Auth0Config = environment.auth0;

  constructor(private readonly http: HttpClient, private winRef: WindowRef) {}

  get domain(): string {
    return this.config.domain;
  }

  get clientId(): string {
    return this.config.clientId;
  }

  get audience(): string | undefined {
    return this.config.audience;
  }

  get redirectUri(): string {
    return `${this.winRef.location.origin}${this.config.redirectUri}`
  }

  get logoutPath(): string {
    return `${this.winRef.location.origin}${this.config.logoutUrl}`
  }

  private generateRandomString(length: number, charset?: string): string {
    const chars = charset ?? 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    const randomValues = new Uint8Array(length);
    crypto.getRandomValues(randomValues);
    for (let i = 0; i < length; i++) {
      result += chars[randomValues[i] % chars.length];
    }
    return result;
  }

  private async sha256Base64Url(input: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = new Uint8Array(hashBuffer);
    const base64 = btoa(String.fromCharCode(...hashArray));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  /**
   * Builds the Auth0 authorize URL using Authorization Code flow with PKCE.
   * Call this before redirecting to Auth0; code_verifier is stored for the callback.
   */
  async getAuthorizeUrl(): Promise<string> {
    const codeVerifier = this.generateRandomString(64);
    const codeChallenge = await this.sha256Base64Url(codeVerifier);
    const state = this.generateRandomString(32);
    const nonce = this.generateRandomString(32);

    sessionStorage.setItem(CODE_VERIFIER_KEY, codeVerifier);
    sessionStorage.setItem(STATE_KEY, state);
    sessionStorage.setItem(NONCE_KEY, nonce);

    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      response_type: 'code',
      scope: 'openid profile email',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      state,
      nonce,
      ...(this.audience ? { audience: this.audience } : {}),
    });

    return `https://${this.domain}/authorize?${params.toString()}`;
  }

  getStoredState(): string | null {
    return sessionStorage.getItem(STATE_KEY);
  }

  getStoredCodeVerifier(): string | null {
    return sessionStorage.getItem(CODE_VERIFIER_KEY);
  }

  clearPkceStorage(): void {
    sessionStorage.removeItem(CODE_VERIFIER_KEY);
    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(NONCE_KEY);
  }

  logOutTheUser() {
  const params = new URLSearchParams({
    client_id: this.clientId,
    returnTo: this.logoutPath,
  });

  window.location.href = `https://${this.domain}/v2/logout?${params.toString()}`;
}

  /**
   * Exchanges the authorization code for tokens (Authorization Code + PKCE).
   * redirectUri must match exactly the value used in the authorize request.
   */
  exchangeCodeForTokens(code: string): Promise<Auth0TokenResponse> {
    const codeVerifier = this.getStoredCodeVerifier();
    if (!codeVerifier) {
      return Promise.reject(new Error('Missing code_verifier; possible duplicate or invalid callback'));
    }

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: this.clientId,
      code_verifier: codeVerifier,
      code,
      redirect_uri: this.redirectUri,
    });

    return firstValueFrom(
      this.http.post<Auth0TokenResponse>(`https://${this.domain}/oauth/token`, body.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      })
    )
      .then((res) => {
        this.clearPkceStorage();
        return res;
      })
      .catch((err) => {
        this.clearPkceStorage();
        return Promise.reject(err);
      });
  }
}
